<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';

const { t } = useI18n();
const { localeTag } = useDateLocale();
const { contentAttributes, content } = useMessageContext();

const isCarousel = computed(
  () => !!contentAttributes.value?.whatsappCarouselTemplate
);
const isMpm = computed(() => !!contentAttributes.value?.whatsappMpmTemplate);

const products = computed(() => {
  const raw = contentAttributes.value?.catalogProducts ?? [];
  return Array.isArray(raw) ? raw : Object.values(raw);
});

const hasContent = computed(() => !!content.value);

function formatPrice(price) {
  if (!price) return '';
  return new Intl.NumberFormat(localeTag.value, {
    style: 'currency',
    currency: 'BRL',
  }).format(parseFloat(price));
}

const buttonLabel = computed(() => {
  if (isMpm.value) return t('CATALOG.TEMPLATE_BUBBLE.VIEW_ITEMS');
  return t('CATALOG.TEMPLATE_BUBBLE.VIEW_CATALOG');
});
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-0 !bg-n-slate-3 !text-n-slate-12"
    data-bubble-name="catalog-template"
  >
    <div class="w-72">
      <!-- CAROUSEL: horizontal scrollable cards each with own button -->
      <template v-if="isCarousel && products.length">
        <div
          class="flex gap-2 px-2 pt-2 pb-0 overflow-x-auto scrollbar-custom snap-x"
        >
          <div
            v-for="(p, i) in products"
            :key="i"
            class="shrink-0 w-52 rounded-lg bg-n-slate-4 overflow-hidden snap-start flex flex-col"
          >
            <div class="w-full h-36 overflow-hidden bg-n-slate-5">
              <img
                v-if="p.imageUrl"
                :src="p.imageUrl"
                :alt="p.title"
                class="w-full h-full object-cover"
              />
              <div
                v-else
                class="w-full h-full flex items-center justify-center"
              >
                <span class="i-lucide-image size-6 text-n-slate-8" />
              </div>
            </div>
            <div class="px-2 py-1.5 flex-1 space-y-0.5">
              <p
                class="text-xs font-bold text-n-slate-12 leading-snug line-clamp-2"
              >
                {{ p.title }}
              </p>
              <p v-if="p.price" class="text-xs font-semibold text-n-slate-11">
                {{ formatPrice(p.price) }}
              </p>
            </div>
            <div class="border-t border-n-slate-6">
              <a
                href="#"
                class="flex items-center justify-center px-2 py-1.5 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-slate-5 transition-colors"
              >
                {{ p.buttonText || t('CATALOG.PRODUCT_BUBBLE.VIEW') }}
              </a>
            </div>
          </div>
        </div>
        <div v-if="hasContent" class="px-3 py-1.5">
          <p class="text-xs text-n-slate-10">{{ content }}</p>
        </div>
      </template>

      <!-- MPM: product list rows + single footer button -->
      <template v-else-if="isMpm">
        <div v-if="hasContent" class="px-3 pt-2.5 pb-1.5">
          <p class="text-xs text-n-slate-12">{{ content }}</p>
        </div>
        <div
          class="flex flex-col gap-1.5 px-2 pb-2"
          :class="{ 'pt-2': !hasContent }"
        >
          <div
            v-for="(p, i) in products"
            :key="i"
            class="flex items-center gap-2 rounded-lg bg-n-slate-4 overflow-hidden"
          >
            <div class="size-14 shrink-0 overflow-hidden bg-n-slate-5">
              <img
                v-if="p.imageUrl"
                :src="p.imageUrl"
                :alt="p.title"
                class="w-full h-full object-cover"
              />
              <div
                v-else
                class="w-full h-full flex items-center justify-center"
              >
                <span class="i-lucide-image size-4 text-n-slate-8" />
              </div>
            </div>
            <div class="flex-1 min-w-0 py-1.5 pr-2">
              <p class="text-xs font-bold text-n-slate-12 truncate">
                {{ p.title }}
              </p>
              <p v-if="p.price" class="text-xs text-n-slate-11">
                {{ formatPrice(p.price) }}
              </p>
            </div>
          </div>
        </div>
        <div class="border-t border-n-slate-6">
          <a
            href="#"
            class="flex items-center justify-center px-2 py-2 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-slate-4 transition-colors"
          >
            {{ buttonLabel }}
          </a>
        </div>
      </template>

      <!-- CATALOG: body text + button -->
      <template v-else>
        <div v-if="hasContent" class="px-3 py-2.5">
          <p class="text-xs text-n-slate-12">{{ content }}</p>
        </div>
        <div class="border-t border-n-slate-6">
          <a
            href="#"
            class="flex items-center justify-center px-2 py-2 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-slate-4 transition-colors"
          >
            {{ buttonLabel }}
          </a>
        </div>
      </template>
    </div>
  </BaseBubble>
</template>
